import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toFieldErrors } from '@/shared/api/submit';
import { formatNumber } from '@/shared/lib/formatNumber';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { BID_STATUS } from '@/shared/constants/bids';
import {
  changeBidCost,
  closeBid,
  downloadAllAccountingFiles,
  moderateWorkOrder,
  rejectServiceFineReturn,
  serviceReturnFine,
  type BidAdditionalWork,
  type BidCustomerAuto,
  type BidDetail,
  type BidWorkOrderHistoryItem,
} from '../detailApi';
import { FileGallery } from './FileGallery';
import { bidKey } from './useBidAction';

const WORK_ORDER_STATUS = { unmoderated: 0, rejected: 1, accepted: 2 } as const;
const EXTRA_STATUS: Record<number, string> = {
  0: 'На согласовании',
  2: 'Одобрено',
  4: 'Отказано',
};

const DataItem = ({ title, data }: { title: string; data?: string }) => (
  <li style={{ listStyleType: 'none' }}>
    <b style={{ marginRight: 10 }}>{title}</b>
    <span>{data || 'Не заполнено'}</span>
  </li>
);

const AdditionalWorksTable = ({ works }: { works: BidAdditionalWork[] }) => (
  <table className="table table-bordered">
    <thead>
      <tr>
        <th>Название работы</th>
        <th>Стоимость</th>
        <th>Скидка</th>
        <th>Запчасти</th>
        <th>Статус</th>
      </tr>
    </thead>
    <tbody>
      {works.map((work) => (
        <tr key={String(work.id)}>
          <td>{work.name}</td>
          <td>{work.cost_work ?? work.cost}</td>
          <td>{work.cost_work_discount}</td>
          <td>{work.cost_parts}</td>
          <td>{EXTRA_STATUS[Number(work.status_id)] ?? work.status_id}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

type Props = {
  bid: BidDetail;
};

export const WorkOrderSection = ({ bid }: Props) => {
  const qc = useQueryClient();
  const workOrder = bid.work_order;
  const statusId = Number(workOrder?.status_id);
  const customerAuto: BidCustomerAuto = workOrder?.customer_auto ?? {};
  const history = bid.work_order_history ?? [];
  const lastEdit: BidWorkOrderHistoryItem | undefined = history.length
    ? history[history.length - 1]
    : undefined;
  const additionalWorks = workOrder?.additional_works ?? [];
  const rejectedWorks = workOrder?.rejected_works ?? [];
  const vehicleImages = workOrder?.vehicle_photos ?? [];
  const workOrderFiles = workOrder?.workorder_files ?? [];
  const totalExtra = additionalWorks.reduce((sum, w) => sum + Number(w.cost ?? w.cost_work ?? 0), 0);

  const [cost, setCost] = useState('');
  const [comment, setComment] = useState('');
  const [fineComment, setFineComment] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const invalidate = () => void qc.invalidateQueries({ queryKey: bidKey(bid.id, 'view') });

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      setMessage('Успех');
      setError('');
      invalidate();
    },
    onError: (err) => {
      setMessage('');
      setError(Object.values(toFieldErrors(err)).flat().join('; ') || 'Ошибка');
    },
  });

  const ownerName = [customerAuto.owner_last_name, customerAuto.owner_first_name, customerAuto.owner_patronymic]
    .filter(Boolean)
    .join(' ');
  const isUnmoderated = statusId === WORK_ORDER_STATUS.unmoderated;
  const isAccepted = statusId === WORK_ORDER_STATUS.accepted;
  const isRejected = statusId === WORK_ORDER_STATUS.rejected;
  const inWork = Number(bid.status_id) === BID_STATUS.IN_WORK;
  const hasFine = Boolean(bid.fine);
  const fineReturnRequested = Boolean(bid.fine?.is_request_fine);

  return (
    <div className="box box-primary">
      <div className="box-header with-border">
        <h3 className="box-title">Данные по заказ наряду</h3>
      </div>
      <div className="box-body">
        <ul style={{ paddingLeft: 0 }}>
          <DataItem title="ФИО:" data={ownerName} />
          <DataItem
            title="Пробег:"
            data={customerAuto.mileage != null && customerAuto.mileage !== '' ? formatNumber(Number(customerAuto.mileage)) : ''}
          />
          <DataItem title="VIN код:" data={customerAuto.vin} />
          <DataItem title="Госномер:" data={customerAuto.licence_plate} />
        </ul>
        {lastEdit?.create_time ? (
          <>
            <div style={{ marginBottom: 10 }}>
              <b>Изменено {formatUnixDateTime(lastEdit.create_time)}. Предыдущие значения:</b>
            </div>
            <ul style={{ paddingLeft: 0 }}>
              {'car_mileage' in (lastEdit.old_data ?? {}) && (
                <DataItem title="Пробег:" data={formatNumber(Number(lastEdit.old_data?.car_mileage))} />
              )}
              {'car_vin' in (lastEdit.old_data ?? {}) && (
                <DataItem title="VIN код:" data={String(lastEdit.old_data?.car_vin ?? '')} />
              )}
              {'car_licence_plate' in (lastEdit.old_data ?? {}) && (
                <DataItem title="Госномер:" data={String(lastEdit.old_data?.car_licence_plate ?? '')} />
              )}
            </ul>
          </>
        ) : null}

        <div style={{ marginTop: 12 }}>
          <FileGallery files={vehicleImages} />
        </div>

        <div style={{ marginTop: 16 }}>
          <b>Дополнительные работы от СТО</b>
          <AdditionalWorksTable works={additionalWorks} />
          <p>
            <b>Общая стоимость доп.работ: {totalExtra}</b>
          </p>
        </div>
        <div>
          <b>Отказанные работы</b>
          <AdditionalWorksTable works={rejectedWorks} />
        </div>

        <div style={{ marginTop: 16 }}>
          <b>Документы</b>
          <div style={{ marginTop: 8 }}>
            <FileGallery files={workOrderFiles} />
          </div>
          <button
            type="button"
            className="btn btn-default btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => downloadAllAccountingFiles(bid.id)}
          >
            Скачать все файлы
          </button>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          {isUnmoderated && (
            <>
              <div className="col-md-2">
                <button
                  type="button"
                  className="btn btn-warning"
                  disabled={run.isPending}
                  onClick={() => run.mutate(() => moderateWorkOrder(bid.id, 'rejected'))}
                >
                  Выписать штраф
                </button>
              </div>
              <div className="col-md-2">
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={run.isPending}
                  onClick={() => run.mutate(() => moderateWorkOrder(bid.id, 'approved'))}
                >
                  Подтверждение данных
                </button>
              </div>
            </>
          )}
          {isAccepted && (
            <div className="col-md-2">
              <span className="label label-success">Заказ-наряд принят</span>
            </div>
          )}
          {isRejected && (
            <div className="col-md-2">
              <span className="label label-danger">Заказ-наряд отклонён</span>
            </div>
          )}
          {!bid.is_closed_by_operator && inWork && (
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-danger"
                disabled={run.isPending}
                onClick={() => run.mutate(() => closeBid(bid.id))}
              >
                Закрыть ремонт
              </button>
            </div>
          )}
          {Boolean(bid.is_closed_by_operator) && (
            <div className="col-md-3">Закрыто оператором комиссия 10%</div>
          )}
        </div>

        {bid.service_id != null && (
          <div className="form-inline" style={{ marginTop: 16 }}>
            <input
              className="form-control"
              placeholder="Новая стоимость"
              value={cost}
              onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ''))}
              style={{ marginRight: 8 }}
            />
            <input
              className="form-control"
              placeholder="Комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{ marginRight: 8, width: 260 }}
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={!cost || run.isPending}
              onClick={() =>
                run.mutate(() => changeBidCost(bid.id, bid.service_id!, cost, comment))
              }
            >
              Изменить стоимость
            </button>
          </div>
        )}

        {hasFine && fineReturnRequested && bid.service_id != null && (
          <div style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="control-label">Комментарий к отказу в возврате штрафа</label>
              <textarea
                className="form-control"
                maxLength={255}
                value={fineComment}
                onChange={(e) => setFineComment(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-success"
              style={{ marginRight: 8 }}
              disabled={run.isPending}
              onClick={() => run.mutate(() => serviceReturnFine(bid.id, bid.service_id!))}
            >
              Вернуть штраф
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={!fineComment.trim() || run.isPending}
              onClick={() =>
                run.mutate(() => rejectServiceFineReturn(bid.id, bid.service_id!, fineComment.trim()))
              }
            >
              Отказать в возврате
            </button>
            {bid.fine?.comment && <p className="help-block">Комментарий: {bid.fine.comment}</p>}
          </div>
        )}

        {message && <span className="label label-success" style={{ marginLeft: 8 }}>{message}</span>}
        {error && <span className="label label-danger" style={{ marginLeft: 8 }}>{error}</span>}
      </div>
    </div>
  );
};
